import React from 'react';
import { companyInfo } from '../data/mock';
import { Building2, TrendingUp, Globe2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export const AboutPage = () => {
  const features = [
    {
      icon: Building2,
      title: 'Established Excellence',
      description: 'Over 10 years of manufacturing experience delivering quality products'
    },
    {
      icon: Globe2,
      title: 'Global Reach',
      description: 'Exporting to Saudi Arabia, Dubai, Peru, USA and expanding worldwide'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Constantly innovating and expanding our product range'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Dedicated to meeting unique requirements of every client'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900">
              About Us
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Leading manufacturer of premium industrial hoses and pipes
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="w-24 h-1.5 bg-[#D8461D] rounded-full"></div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Our Story
              </h2>
              <div className="space-y-4 text-lg text-slate-700 leading-relaxed">
                <p>
                  {companyInfo.name} is a leading manufacturer of {companyInfo.tagline.toLowerCase()}. 
                  We specialize in producing high-quality products that meet the demanding requirements 
                  of various industrial and agricultural sectors.
                </p>
                <p>
                  Our commitment to quality and customer satisfaction has enabled us to establish a strong 
                  presence in international markets including {companyInfo.exports.join(', ')}. We take 
                  pride in our manufacturing excellence and continuous innovation.
                </p>
                <p>
                  With state-of-the-art manufacturing facilities at {companyInfo.address} and a dedicated team 
                  of professionals, we deliver solutions that ensure reliability, durability, and performance in 
                  the most challenging environments.
                </p>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1647427060118-4911c9821b82"
                  alt="Manufacturing Facility"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              What sets us apart in the industrial hose manufacturing industry
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-md border border-slate-200 hover:shadow-xl transition-shadow duration-300 space-y-4"
                >
                  <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center">
                    <IconComponent size={28} className="text-[#D8461D]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Export Destinations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Global Presence
              </h2>
              <p className="text-lg text-slate-600">
                Our products reach customers across multiple continents
              </p>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-slate-50 rounded-2xl p-8 border border-orange-100">
              <h4 className="font-bold text-slate-900 mb-6 text-xl flex items-center justify-center gap-2">
                <Globe2 size={24} className="text-[#D8461D]" />
                Export Destinations
              </h4>
              <div className="flex flex-wrap gap-4 justify-center">
                {companyInfo.exports.map((country, index) => (
                  <div
                    key={index}
                    className="px-6 py-4 bg-white rounded-xl font-semibold text-lg border-2 border-orange-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="text-[#D8461D]">{country}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-md border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To be the preferred global supplier of industrial hoses and pipes by delivering 
                innovative, high-quality products that exceed customer expectations and contribute 
                to the success of industries worldwide.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-md border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Values</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-[#D8461D] font-bold">•</span>
                  <span>Quality and excellence in everything we do</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D8461D] font-bold">•</span>
                  <span>Customer satisfaction as our top priority</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D8461D] font-bold">•</span>
                  <span>Innovation and continuous improvement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D8461D] font-bold">•</span>
                  <span>Integrity and transparency in all operations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#D8461D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h3 className="text-3xl lg:text-4xl font-bold text-white">
            Ready to Work With Us?
          </h3>
          <p className="text-xl text-orange-100">
            Join hundreds of satisfied customers worldwide
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button size="lg" className="bg-white text-[#D8461D] hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
                View Products
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-#b83817 px-8 py-6 text-lg font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
